/* eslint-disable camelcase */
import React, { useContext, useMemo } from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { createSelector } from 'reselect';
import { FiltersContext } from 'app/containers/contexts/filters.context/filters.context';
import { Transition } from 'react-transition-group';

import {
    CATEGORIES_ORDER_MOBILE,
    CATEGORY_MAIN,
    CATEGORY_MOST_INTRESTING,
    CATEGORY_MOST_INTRESTING_TAG_ID,
    CATEGORY_TITLES_MOBILE,
    isCategoryVisibleForLocation,
    mostInterestingCategoryTitleForABTest,
} from 'app/const/categories';

import { selectDishes } from 'app/utils/dish';

import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { MenuDispatchContext } from 'app/containers/contexts/menu.context';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { AuthContext } from 'app/containers/AuthContainer';
import { DesignContext } from 'app/containers/DesignProvider';
import { Mobile, Desktop } from 'app/components/Responsive';
import BonduelleDesktop from 'app/components/banners/BonduelleDesktop';
import '../../../../../../components/MenuCategoryList/menu-category-list.scss';
import {
    isDesktop,
} from 'app/utils/resolution';
import { piecewiseBezier } from 'app/utils/smoothScrollAnimationUtils';
import { convertArrayIntoLinkedList } from 'app/utils/covertArrayIntoLinkedList';
import { useRouterState } from 'app/hooks/useRouterState';
import { abTestDataContext } from 'app/containers/contexts/abTest.context';
import { useDisabledDatesSelected } from 'app/hooks';
import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { SetsMobileSlider } from 'app/components/SetsMobileSlider';
import { useFlatDishesList, usePromoCompoentPosition } from 'app/components/MenuCategoryList/hooks';
import { getDishesByC_ID } from 'app/components/MenuCategoryList/hooks/useFlatDishesList';
import { useBasketData, useCategoryLocationKey } from 'app/hooks/accessors';
import { gql, useFragment, useReactiveVar } from '@apollo/client';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { doc } from 'prettier';
import MenuCategoryAdapter from './MenuCategoryV2';

import {
    MENU_CATEGORY_LIST_V2_DISCOUNT_CONDITIONS_FRAGMENT,
} from './graphql/discountConditionsFragment.graphql';

const viewportPaddingSelector = createSelector(
    (headerHeight) => headerHeight,
    (headerHeight) => ({ top: headerHeight + 400, bottom: 300 }),
);

const APPEARING_TIMEOUT = 150;

const LAST_STEP_SMOOTH_ANIMATION_DURATION = 650;
const BEZIER_ANIMATION_POINTS = [0, 0.80, 0.85, 0.90, 0.95, 0.96, 0.97, 0.98, 0.99, 0.99, 1];

function getCategoriesLinkedList() {
    const categoriesNodes = document.querySelectorAll('.mainCategoryRoot');

    if (!categoriesNodes) return [];

    const categories = Array.from(categoriesNodes);
    const categoriesLinkedList = convertArrayIntoLinkedList(categories);
    return categoriesLinkedList;
}

const checkIsMenuDishesChanges = (dishes) => {
    const IDs_list = dishes.map((itm) => itm.id);
    const hash = IDs_list.join(':');
    return hash;
};

class MenuCategoryListV2 extends React.Component {
    constructor(props) {
        super(props);

        const { menuDishesQuery } = props;

        this.state = {
            isDesktopBannerVisible: true,
            menuDishesQuery,
        };

        this.isProgrammaticScroll = false;
        this.isObserverInited = false;
        this.isMainDishesRenderedTriggered = false;
        this.isScrolledByUser = false;
        this.activeCategoryIndex = 0;
        this.activeCategoryId = null;
        this.dishesContainerRef = React.createRef();
        this.mobileBestSetsRef = React.createRef();

        this.isIntersectingOn = !isDesktop();
    }

    componentDidMount() {
        const {
            history,
        } = this.props;

        this.resolveMainDishesLoadedState();

        this.initObserver();
        document.addEventListener('categoryChangeFromSlider', this.handleCategoryChange);
    }

    componentWillUnmount() {
        this.observer?.disconnect();
        document.removeEventListener('categoryChangeFromSlider', this.handleCategoryChange);
    }

    initObserver() {
        this.observer = new IntersectionObserver(this.handleCategoryIntersection, {
            rootMargin: `-${window.innerHeight / 2 - 20 / 2}px 0px`,
            threshold: 0,
        });
        this.observeCategoriesScrolling();
    }

    handleUserScrolling() {
        if (!this.isScrolledByUser) {
            this.isScrolledByUser = true;
        }
    }

    observeCategoriesScrolling() {
        const { isDelivery } = this.props;
        if (isDelivery) return null;

        let counter = 0;

        const addListenersToNodes = () => {
            const categoriesNodes = document.querySelectorAll('.mainCategoryRoot');
            const otherTrackableElements = document.querySelectorAll('[data-scroll_name]');
            const otherBlocks = Array.from(otherTrackableElements);
            if (otherBlocks) {
                otherBlocks.forEach((c) => this.observer.observe(c));
            }
            if (categoriesNodes) {
                const trackableBlocks = Array.from(categoriesNodes);
                trackableBlocks.forEach((c) => this.observer.observe(c));
            }
            if (categoriesNodes.length && counter > 2) {
                this.isObserverInited = true;
            }

            counter += 1;
        };

        const interval = setInterval(
            () => {
                if (!this.isObserverInited) {
                    addListenersToNodes();
                    // console.log('observer not inited');
                } else {
                    // console.log('observer inited');
                    clearInterval(interval);
                }
            },
            500,
        );
    }

    handleCategoryIntersection = (entries) => {
        const {
            menuDispatchContext,
            onScrollElementEntered,
        } = this.props;
        entries.forEach((entry) => {
            const isIntersectionDetecting = !this.isProgrammaticScroll && entry.isIntersecting;
            if (!isIntersectionDetecting) return;

            const categoryId = entry.target.dataset.category_id;
            const scrollName = entry.target.dataset?.scroll_name;
            if (scrollName) {
                onScrollElementEntered(scrollName);
            }
            if (!categoryId) return;
            this.replaceCategoryUrlState(categoryId);

            menuDispatchContext({ type: 'intersectCategory', payload: { categoryId } });
            this.handleCategoryViewportPositionChange({ categoryId });

            const categories = getCategoriesLinkedList();
            if (!categories) return;
            const targetCategory = categories.find((e) => e.value.dataset.category_id === categoryId);
            this.activeCategoryIndex = targetCategory.index;
        });
    };

    replaceCategoryUrlState = () => {
        if (isDesktop() && this.isScrolledByUser) return;
        const { history } = this.props;
        const currentParams = queryString.parse(history.location.search);

        delete currentParams.mc;
        const newSearch = queryString.stringify(currentParams);

        const detailPageRegex = /dish\/\d+\//;
        const isDetailPageOpened = detailPageRegex.test(window.location.pathname);
        const isMenu = window.location.pathname.includes('menu');
        if (history && !isDetailPageOpened && isMenu) {
            history?.push(`/menu/?${newSearch}`);
        }
    };

    handleCategoryChange = (event) => {
        const { activeCategoryId } = event.detail;

        this.isProgrammaticScroll = true;
        sessionStorage.setItem('isProgrammaticScroll', 'true');
        const isProgrammaticScrollWaitingTimeout = 800;

        const categoriesLinkedList = getCategoriesLinkedList();
        const targetCategory = categoriesLinkedList?.find((category) => category.value.dataset.category_id === activeCategoryId);

        if (targetCategory) {
            this.dispatchScrollingChainAnimation(targetCategory);
            setTimeout(() => {
                this.isProgrammaticScroll = false;
                sessionStorage.setItem('isProgrammaticScroll', 'false');
            }, isProgrammaticScrollWaitingTimeout);
        }
    };

    dispatchScrollingChainAnimation(targetCategory) {
        const { menuDispatchContext } = this.props;
        const isDesktopResolution = isDesktop();

        if (!isDesktopResolution) {
            this.mobileScrollCategoryToTop(targetCategory);
        } else {
            menuDispatchContext({
                type: 'scrollTo',
                payload: {
                    value: this.activeCategoryId,
                    target: 'category',
                },
            });
        }
        this.activeCategoryIndex = targetCategory.index;
    }

    mobileScrollCategoryToTop(targetCategory) {
        const scrollDirection = targetCategory.index > this.activeCategoryIndex
            ? 'down'
            : 'up';

        const mobileOffsetPadding = 120;
        const nextTopOffset = targetCategory.next;
        const prevTopOffset = targetCategory.prev;
        const topOffset = targetCategory.value.getBoundingClientRect().top
            + window.pageYOffset
            - mobileOffsetPadding;

        const currentNeighbour = scrollDirection === 'up'
            ? nextTopOffset
            : prevTopOffset;
        const scrollingToNeighbourDistance = currentNeighbour.value
            ? currentNeighbour.value.getBoundingClientRect().top + window.pageYOffset
            : null;

        if (scrollingToNeighbourDistance) {
            window.scrollTo(0, scrollingToNeighbourDistance);
        }
        this.smoothScrollTo(
            topOffset,
            LAST_STEP_SMOOTH_ANIMATION_DURATION,
            null,
        );
        this.handleUserScrolling();
    }

    smoothScrollTo(y, duration, callback) {
        const startY = window.pageYOffset;
        const diff = y - startY;
        const start = performance.now();

        function step(timestamp) {
            const time = (timestamp - start) / duration;
            const t = Math.min(time, 1);

            const easedT = piecewiseBezier(
                t,
                BEZIER_ANIMATION_POINTS,
            );

            window.scrollTo({ top: startY + diff * easedT });

            if (t < 1) {
                window.requestAnimationFrame(step);
            } else if (callback) {
                callback();
            }
        }

        window.requestAnimationFrame(step);
    }

    shouldComponentUpdate(nextProps) {
        const { isMobileMenuDishesVisible } = nextProps;

        return isMobileMenuDishesVisible;
    }

    componentDidUpdate(prevProps) {
        this.updateDishesAnimationState(prevProps);
        this.resolveMainDishesLoadedState();
    }

    // eslint-disable-next-line
    resolveMainDishesLoadedState() {
        const {
            menuDishesQuery: { allDishes },
            onMainDishesRendered,
        } = this.props;

        if (this.isMainDishesRenderedTriggered) return;

        const mainDishes = selectDishes(allDishes, null, [], [CATEGORY_MAIN]);

        if (mainDishes.length === 0) {
            onMainDishesRendered(0);
        }

        this.isMainDishesRenderedTriggered = true;

        const availableDishesCount = mainDishes.filter((d) => d.visibleStatus === 1).length;
        onMainDishesRendered(availableDishesCount);
    }

    handleCloseDesktopBanner = () => {
        this.setState({
            isDesktopBannerVisible: false,
        });
    };

    handleCategoryViewportPositionChange = ({ categoryId, scrollName, viewportState }) => {
        const { onCategoryEntered } = this.props;
        onCategoryEntered(categoryId);
        // if (viewportState.state === 'entered') onCategoryEntered(categoryId);
    };

    updateDishesAnimationState(prevProps) {
        const {
            menuDishesQuery,
        } = this.props;
        const {
            menuDishesQuery: prevMenuDishesQuery,
        } = prevProps;

        const next_dishes_hash = checkIsMenuDishesChanges(menuDishesQuery.menuDishes);
        const prev_dishes_hash = checkIsMenuDishesChanges(prevMenuDishesQuery.menuDishes);

        const isMenuDishesChanged = next_dishes_hash !== prev_dishes_hash;
        const dishesIds = new Set(menuDishesQuery.filtredDishes.map((d) => d.id));
        const prevDishesIds = new Set(prevMenuDishesQuery.filtredDishes.map((d) => d.id));
        const isMenuDishesFiltred = !isEqual(dishesIds, prevDishesIds);

        void isMenuDishesChanged;
        void isMenuDishesFiltred;
    }

    renderMenuCategoryList(transitionState) {
        const {
            designContext: { headerHeight },

            selectedFilters: {
                selectedCategories,
                selectedCategory,
                selectedTags,
                selectedPeriod,
            },
            isDishesUpdating,
            localeContext: { locale },
            locationKey,
            history,
            setTemporaryBasketPeriod,
            onSetPeriod,
            isDelivery,
            isSelectedDateInEditableRange,
            filterPeriod,
            openBasket,
            thirdDishId,
            fifthDishId,
            sixthDishId,
            seventhDishId,
            tenthDishId,
            thirteenthDishId,
            selectionPositions,
            updatePromoCompoentPosition,
            intrstDishesIdList,
            isDisabledDatesSelected,
            flatDishesList,
            isCartEmpty,
            segment,
            menuDishesQuery,
            isDisabledDateSelected,
        } = this.props;

        const dishesProps = {
            ...omit(this.props, [
                'filterQuery',
                'isFiltersOpen',
                'selectedFilters',
                'authData',
                'selectedFilterPeriod',
            ]),
            useLazyLoading: true,
        };

        const isFilterPeriodSelected = filterPeriod === selectedPeriod;
        const canEditTags = isFilterPeriodSelected ? isSelectedDateInEditableRange : true;

        const enabledCategories = CATEGORIES_ORDER_MOBILE.filter((c) => (
            selectedCategories.includes(c)
            && isCategoryVisibleForLocation(c, locationKey)
        ));

        if (!menuDishesQuery?.menuDishes) return null;

        const { loading, error, menuDishes: allDishes } = menuDishesQuery;

        const listClasses = classNames({
            'menu-category-list': true,
            [transitionState]: true,
        });
        const dishesContainerClasses = classNames({
            'menu-category-container': true,
            with_new_categories: true,
            'is-dishes-updating': isDishesUpdating,
            withoutSlider: isDisabledDatesSelected,
            delivery: segment === 'delivery',
        });

        const bannerClasses = classNames({
            'menu-category-list__banner': true,
            slider: true,
        });

        const canShowSelections = !isDelivery && canEditTags && selectedPeriod !== '2021-12-29';

        const mostIntrestingDishesData = {
            loading,
            error,
            menuDishes: selectDishes(
                allDishes,
                null,
                [CATEGORY_MOST_INTRESTING_TAG_ID, ...selectedTags],
                selectedCategories,
            ),
        };

        const isMostIntrestingCategoryShown = CATEGORIES_ORDER_MOBILE.includes(CATEGORY_MOST_INTRESTING)
            && selectedCategories.includes(CATEGORY_MOST_INTRESTING)
            && mostIntrestingDishesData.menuDishes.length > 0;

        return (
            <div styleName={listClasses}>
                <div
                    styleName={dishesContainerClasses}
                    ref={this.dishesContainerRef}
                >
                    {!isDelivery && (
                        <Mobile>
                            <div styleName={bannerClasses} ref={this.mobileBestSetsRef}>
                                <SetsMobileSlider
                                    showTrialSet
                                    isBasketEmpty={isCartEmpty}
                                    history={history}
                                    setTemporaryBasketPeriod={setTemporaryBasketPeriod}
                                    onSetPeriod={onSetPeriod}
                                    openBasket={openBasket}
                                    selectedPeriod={selectedPeriod}
                                    isMenuEmpty={false}
                                />
                            </div>
                        </Mobile>
                    )}
                    {isMostIntrestingCategoryShown && !isDisabledDateSelected && (
                        <div className="mainCategoryRoot" data-category_id={CATEGORY_MOST_INTRESTING}>
                            <MenuCategoryAdapter
                                key={CATEGORY_MOST_INTRESTING}
                                index={1}
                                {...dishesProps}
                                flatDishesList={flatDishesList}
                                headerHeight={headerHeight}
                                menuDishesQuery={mostIntrestingDishesData}
                                categoryId={CATEGORY_MOST_INTRESTING}
                                isCatgoryActive={CATEGORY_MOST_INTRESTING === selectedCategory}
                                isIntersectingOn={this.isIntersectingOn}
                                title={mostInterestingCategoryTitleForABTest[locale][CATEGORY_MOST_INTRESTING]}
                                viewportPadding={viewportPaddingSelector(headerHeight)}
                                onViewportPositionChange={this.handleCategoryViewportPositionChange}
                                isDishesUpdating={isDishesUpdating}
                                locale={locale}
                                isDishesSliderNeeded={canShowSelections}
                                thirdDishId={thirdDishId}
                                sixthDishId={sixthDishId}
                                seventhDishId={seventhDishId}
                                tenthDishId={tenthDishId}
                                thirteenthDishId={thirteenthDishId}
                                selectionPositions={selectionPositions}
                                updatePromoCompoentPosition={updatePromoCompoentPosition}
                            />
                        </div>
                    )}
                    {!isDisabledDateSelected && enabledCategories.map((categoryId, index) => {
                        const menuDishes = getDishesByC_ID(allDishes, categoryId, intrstDishesIdList);

                        if (!menuDishes.length) return null;

                        const title = CATEGORY_TITLES_MOBILE[locale][categoryId];

                        const dishesData = {
                            loading,
                            error,
                            menuDishes,
                        };
                        const isDishesSliderNeeded = (
                            categoryId === CATEGORY_MAIN
                            && !isMostIntrestingCategoryShown
                            && canShowSelections
                        );
                        return (
                            <div className="mainCategoryRoot" data-category_id={categoryId} key={categoryId}>
                                <MenuCategoryAdapter
                                    key={categoryId}
                                    index={isMostIntrestingCategoryShown ? index + 1 : index}
                                    {...dishesProps}
                                    headerHeight={headerHeight}
                                    menuDishesQuery={dishesData}
                                    categoryId={categoryId}
                                    isCatgoryActive={categoryId === selectedCategory}
                                    title={title}
                                    viewportPadding={viewportPaddingSelector(headerHeight)}
                                    onViewportPositionChange={this.handleCategoryViewportPositionChange}
                                    isDishesUpdating={isDishesUpdating}
                                    locale={locale}
                                    isDishesSliderNeeded={isDishesSliderNeeded}
                                    enabledCategories={enabledCategories}
                                    thirdDishId={thirdDishId}
                                    fifthDishId={fifthDishId}
                                    sixthDishId={sixthDishId}
                                    seventhDishId={seventhDishId}
                                    tenthDishId={tenthDishId}
                                    thirteenthDishId={thirteenthDishId}
                                    selectionPositions={selectionPositions}
                                    updatePromoCompoentPosition={updatePromoCompoentPosition}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        return (
            <Transition
                in
                appear
                timeout={APPEARING_TIMEOUT}
            >
                {(transitionState) => this.renderMenuCategoryList(transitionState)}
            </Transition>
        );
    }
}

const CART_DATA_FOR_CATEGORY_LIST_QUERY = gql`
    query CART_DATA_FOR_CATEGORY_LIST(
        $period: String!
    ) {
        cart(
            period: $period
        ) {
            type
            period
            sections {
                id
                cartType
            }
        }
    }
`;

const MenuCategoryListAdapter = (props) => {
    const {
        openBasket = null,
        isFiltersOpen = false,
        isMenuPromoShown = false,
        isDelivery = false,
        onMainDishesRendered = () => { },
    } = props;

    const period = useReactiveVar(selectedPeriodVar);

    const { data: { isPromoCodeActivated, discountType } } = useFragment({
        fragment: MENU_CATEGORY_LIST_V2_DISCOUNT_CONDITIONS_FRAGMENT,
        from: {
            __ref: `DiscountConditions:${period}`,
        },
    });


    const [routerState] = useRouterState();
    const { selectedFilters } = useContext(FiltersContext);
    const { history, location } = routerState;

    const { cart } = useBasketData(CART_DATA_FOR_CATEGORY_LIST_QUERY);

    const {
        state: {
            eeAvailableDates, firstVisibleDates, filterPeriod, prevSelectedFilterPeriod,
            selectedFilterPeriod,
            isSelectedDateInEditableRange,
            selectedDate,
        },
    } = useContext(menuDatesState);
    const menuDispatchContext = useContext(MenuDispatchContext);
    const localeContext = useContext(LocaleContext);
    const abTestData = useContext(abTestDataContext);
    const designContextValue = useContext(DesignContext);
    const authData = useContext(AuthContext);
    const locationKey = useCategoryLocationKey();

    const isDisabledDatesSelected = useDisabledDatesSelected();

    const {
        flatDishesList,
        dishesHashSum,
        intrstDishesIdList,
    } = useFlatDishesList(props);

    const {
        thirdDishId,
        fifthDishId,
        sixthDishId,
        seventhDishId,
        ninthDishId,
        eleventhDishId,
        tenthDishId,
        thirteenthDishId,
        selectionPositions,
        updatePromoCompoentPosition,
    } = usePromoCompoentPosition({ flatDishesList, dishesHashSum });

    const isCartEmpty = useMemo(
        () => cart?.sections?.length > 0 || true,
        [cart?.sections?.length],
    );

    const { segment } = useContext(PathSegmentContext);

    return (
        <MenuCategoryListV2
            {...props}
            isPromoCodeActivated={isPromoCodeActivated}
            discountType={discountType}
            isDisabledDateSelected={isDisabledDatesSelected}
            period={period}
            history={history}
            flatDishesList={flatDishesList}
            selectedFilters={selectedFilters}
            location={location}
            locationKey={locationKey}
            localeContext={localeContext}
            abTestDataContext={abTestData}
            menuDispatchContext={menuDispatchContext}
            designContext={designContextValue}
            authData={authData}
            eeAvailableDates={eeAvailableDates}
            firstVisibleDates={firstVisibleDates}
            filterPeriod={filterPeriod}
            selectedDate={selectedDate}
            prevSelectedFilterPeriod={prevSelectedFilterPeriod}
            selectedFilterPeriod={selectedFilterPeriod}
            isSelectedDateInEditableRange={isSelectedDateInEditableRange}
            openBasket={openBasket}
            isFiltersOpen={isFiltersOpen}
            isMenuPromoShown={isMenuPromoShown}
            isDelivery={isDelivery}
            onMainDishesRendered={onMainDishesRendered}
            thirdDishId={thirdDishId}
            fifthDishId={fifthDishId}
            sixthDishId={sixthDishId}
            seventhDishId={seventhDishId}
            ninthDishId={ninthDishId}
            tenthDishId={tenthDishId}
            eleventhDishId={eleventhDishId}
            thirteenthDishId={thirteenthDishId}
            selectionPositions={selectionPositions}
            updatePromoCompoentPosition={updatePromoCompoentPosition}
            intrstDishesIdList={intrstDishesIdList}
            isDisabledDatesSelected={isDisabledDatesSelected}
            isCartEmpty={isCartEmpty}
            segment={segment}
        />
    );
};

export default MenuCategoryListAdapter;

MenuCategoryListV2.propTypes = {
    designContext: PropTypes.shape({
        headerHeight: PropTypes.number.isRequired,
    }).isRequired,

    localeContext: PropTypes.shape({
        locale: PropTypes.string.isRequired,
    }).isRequired,

    locationKey: PropTypes.string.isRequired,

    history: PropTypes.shape({}).isRequired,

    isMenuPromoShown: PropTypes.bool,
    isFiltersOpen: PropTypes.bool,

    selectedFilterPeriod: PropTypes.string,
    filterPeriod: PropTypes.string.isRequired,

    filterQuery: PropTypes.shape({
        menuFilter: PropTypes.shape({
            periods: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }).isRequired,

    menuDishesQuery: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.shape({}),
        menuDishes: PropTypes.arrayOf(PropTypes.shape({})),
        allDishes: PropTypes.arrayOf(PropTypes.shape({})),
        filtredDishes: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    authData: PropTypes.shape({
        isAuthed: PropTypes.bool,
    }).isRequired,

    onCategoryEntered: PropTypes.func.isRequired,
    openBasket: PropTypes.func,
    openDishDetails: PropTypes.func.isRequired,

    isDelivery: PropTypes.bool,
    onMainDishesRendered: PropTypes.func,

    setDishesUpdatingState: PropTypes.func.isRequired,
    setTemporaryBasketPeriod: PropTypes.func.isRequired,
    onSetPeriod: PropTypes.func.isRequired,
    isDishesUpdating: PropTypes.bool.isRequired,
    firstVisibleDates: PropTypes.shape({
        eeAvailableDates: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    isSelectedDateInEditableRange: PropTypes.bool.isRequired,

    isMobileMenuDishesVisible: PropTypes.bool.isRequired,
};

